



























import {
  ref,
  computed,
  useContext,
  defineComponent,
  useFetch,
  onMounted,
  watch
} from '@nuxtjs/composition-api';
import mergeWith from 'lodash-es/mergeWith';
import { useCache, CacheTagPrefix } from '@wemade-vsf/cache';
import { getBreadcrumbs, getProductSku } from '@wemade-vsf/catalog/lib/runtime/product/getters/productGetters';
import { useProduct } from '@wemade-vsf/catalog/lib/runtime/product/composables/useProduct';
import { usePageStore, getMetaInfo } from '@wemade-vsf/core';
import { ProductTypeEnum } from '@wemade-vsf/catalog/lib/runtime/product/enums/ProductTypeEnum';
import { useEvent } from '@wemade-vsf/composables';
import { useWishlist } from '@wemade-vsf/wishlist';
import { useWmAttributes } from '@wemade-vsf/catalog/lib/runtime/product/composables/useWmAttributes';
import { usePriceRequest } from '@wemade-vsf/catalog/lib/runtime/product/composables/usePriceRequest';
import type { Product, ProductAttachmentInterface } from '@wemade-vsf/catalog/lib/runtime/product/types';
import type { Breadcrumb } from '@wemade-vsf/catalog/lib/runtime/types';
import { useProductAttachments } from '@wemade-vsf/catalog/lib/runtime/product/composables/useProductAttachments';
import { useConfig } from '@wemade-vsf/composables';
import { SfBreadcrumbs, SfLoader } from '@storefront-ui/vue';
import ProductSkeleton from 'components/catalog/Product/ProductSkeleton.vue';
import LazyHydrate from 'vue-lazy-hydration';
import ProductTabs from 'components/catalog/Product/ProductTabs.vue';

export default defineComponent({
  name: 'ProductPage',
  components: {
    ProductSkeleton,
    SimpleProduct: () => import('components/catalog/Product/ProductTypes/simple/SimpleProduct.vue'),
    BundleProduct: () => import('components/catalog/Product/ProductTypes/bundle/BundleProduct.vue'),
    ConfigurableProduct: () => import('components/catalog/Product/ProductTypes/configurable/ConfigurableProduct.vue'),
    GroupedProduct: () => import('components/catalog/Product/ProductTypes/grouped/GroupedProduct.vue'),
    SfBreadcrumbs,
    SfLoader,
    ProductTabs,
    LazyHydrate
  },
  transition: 'fade',
  setup() {
    const { routeData } = usePageStore();
    const product = ref<Product | null>(null);
    const { addTags } = useCache();
    const { app, localePath, i18n } = useContext();

    const { getProductDetails, loading: loadingProduct } = useProduct();
    const { isPriceRequestModalOpen } = usePriceRequest();

    const { load: loadAttachments } = useProductAttachments();
    const productAttachments = ref<ProductAttachmentInterface[] | null>(null);

    const { attributeGroups, loadAttributeGroups, loading: loadingGroups, setAttributeGroups } = useWmAttributes();
    const { error: nuxtError } = useContext();
    const { load: loadWishlist } = useWishlist();
    const breadcrumbs = ref<Breadcrumb[]>([])
   
    const loading = computed(() => loadingProduct.value || loadingGroups.value)

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      }
    });

    // eslint-disable-next-line no-underscore-dangle
    const renderer = computed(() => product.value?.__typename ?? ProductTypeEnum.SIMPLE_PRODUCT);
    const uid = computed(() => product.value?.configurable_product_options_selection?.variant?.uid ?? (product.value?.uid ?? null))

    const fetchProductBaseData = async (searchQuery = getBaseSearchQuery()) => {
      const result = await getProductDetails({
        ...searchQuery,
      });

      setAttributeGroups(null);

      product.value = mergeWith({}, product.value, result?.items?.[0] as Product ?? null, (objValue, srcValue) => {
        if (Array.isArray(srcValue)) {
          return srcValue
        }
      });
    };

    const fetchProductExtendedData = async (searchQuery = getBaseSearchQuery()) => {
      const { data } = await app.$vsf.$magento.api.getProductPriceBySku(searchQuery);
      const originalSku = product.value ? getProductSku(product.value) : null;
      
      product.value = mergeWith({}, product.value, data.products?.items?.[0] as Product, (objValue, srcValue) => {
        if (Array.isArray(srcValue)) {
          return srcValue
        }
      });
      if (!product.value) return;
      const newSku = getProductSku(product.value)

      if ((originalSku !== newSku) || !attributeGroups.value) {
        await loadAttributeGroups(newSku)
      }
     
      useEvent('product:view', { product: product.value })
    };

    const { config } = useConfig();
    function getCurrentCategory(categories) {
      const blacklist = config.value.storefront?.breadcrumb_blacklist_categories ?? [];
      if (categories.length === 1) {
        return categories[0];
      }
      const validCategories = categories.filter((category) => !blacklist.includes(category.uid));
      return validCategories.length ? validCategories[0] : categories[0];
    }

    useFetch(async () => {
      await fetchProductBaseData();

      if (Boolean(product?.value?.sku) === false) nuxtError({ statusCode: 404 });

      if (product.value) {
        const productCategories = product.value?.categories ?? [];
        breadcrumbs.value = [
            {
              text: i18n.t('Home'),
              link: localePath('/')
            },
            ...getBreadcrumbs(
              product.value,
              //@ts-ignore
              Array.isArray(productCategories) && productCategories.length ? getCurrentCategory(productCategories) : undefined,
            ).map((breadcrumb) => ({ ...breadcrumb, link: localePath(breadcrumb.link) }))
        ]
      }
     
      if (!attributeGroups.value) {
        const groups = await loadAttributeGroups(getProductSku(product.value))
        setAttributeGroups(groups)
      }

      const tags = [
        {
          prefix: CacheTagPrefix.View,
          value: `product-${routeData.sku}`,
        },
      ];

      const productTags = [{
        prefix: CacheTagPrefix.Product,
        value: product.value.uid,
      }];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      addTags([...tags, ...productTags]);
    });

    onMounted(async () => {
      await Promise.all([fetchProductExtendedData(), loadWishlist()]);
    });

    watch(() => uid.value, async (newVal) => {
      if (uid.value) {
        productAttachments.value = await loadAttachments(uid.value)
      }
    }, { immediate: true })

    return {
      renderer,
      loading,
      attributeGroups,
      breadcrumbs,
      product,
      productAttachments,
      fetchProduct: fetchProductExtendedData,
      isPriceRequestModalOpen
    };
  },
  head() {
    const meta = getMetaInfo(this.product);
    return {
      ...meta,
      bodyAttrs: {
        class: 'product-page'
      }
    }
  },
});
